import { motion } from 'framer-motion';
import { MessageSquare, Users, Github, Twitter, Youtube, ArrowRight, Star, Heart } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

const platforms = [
  {
    icon: MessageSquare,
    name: 'Discord Community',
    description: 'Join 5,000+ members discussing AI, automation, and business growth.',
    members: '5,200+ members',
    color: 'indigo',
    url: 'https://discord.gg/flowai',
  },
  {
    icon: Twitter,
    name: 'Twitter / X',
    description: 'Follow us for daily tips, product updates, and AI insights.',
    members: '12K followers',
    color: 'sky',
    url: 'https://twitter.com/flowai',
  },
  {
    icon: Youtube,
    name: 'YouTube Channel',
    description: 'Watch tutorials, case studies, and behind-the-scenes content.',
    members: '8K subscribers',
    color: 'red',
    url: 'https://youtube.com/@flowai',
  },
  {
    icon: Github,
    name: 'GitHub',
    description: 'Explore our open-source SDKs, examples, and contribute.',
    members: '2K stars',
    color: 'gray',
    url: 'https://github.com/flowai',
  },
];

const featuredPosts = [
  {
    author: 'Sarah M.',
    avatar: '👩‍💼',
    role: 'Marketing Agency Owner',
    content:
      'Just saved 20+ hours this week using Aria for social media management. The content quality is incredible!',
    likes: 124,
    replies: 23,
  },
  {
    author: 'David K.',
    avatar: '👨‍💻',
    role: 'SaaS Founder',
    content:
      'Nova has completely transformed our customer support. Response times went from 2 hours to instant. 🚀',
    likes: 98,
    replies: 15,
  },
  {
    author: 'Emily R.',
    avatar: '👩‍🎨',
    role: 'Freelance Designer',
    content:
      'As a solo freelancer, SweetPilot is like having a whole team. Ink writes my copy, Sage helps with strategy. Game changer!',
    likes: 156,
    replies: 31,
  },
];

const events = [
  {
    title: 'AI for Small Business Webinar',
    date: 'Dec 15, 2024',
    time: '2:00 PM EST',
    type: 'Webinar',
  },
  {
    title: 'Community AMA with Founders',
    date: 'Dec 20, 2024',
    time: '11:00 AM EST',
    type: 'Live Q&A',
  },
  {
    title: 'Advanced Prompt Engineering Workshop',
    date: 'Jan 5, 2025',
    time: '3:00 PM EST',
    type: 'Workshop',
  },
];

const colorClasses: Record<string, { bg: string; text: string; textLight: string; border: string; borderLight: string }> = {
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', textLight: 'text-indigo-600', border: 'border-indigo-500/20', borderLight: 'border-indigo-200' },
  sky: { bg: 'bg-sky-500/10', text: 'text-sky-400', textLight: 'text-sky-600', border: 'border-sky-500/20', borderLight: 'border-sky-200' },
  red: { bg: 'bg-red-500/10', text: 'text-red-400', textLight: 'text-red-600', border: 'border-red-500/20', borderLight: 'border-red-200' },
  gray: { bg: 'bg-gray-500/10', text: 'text-gray-400', textLight: 'text-gray-600', border: 'border-gray-500/20', borderLight: 'border-gray-200' },
};

export default function Community() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  return (
    <StaticPageLayout
      title="Community"
      subtitle="Connect with thousands of entrepreneurs and creators using SweetPilot."
    >
      {/* Platforms */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Join the Conversation</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {platforms.map((platform, index) => {
            const colors = colorClasses[platform.color];
            return (
              <motion.a
                key={platform.name}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`group flex items-start gap-4 p-6 rounded-2xl border transition-all ${
                  isDark
                    ? `bg-dark-800/50 ${colors.border} hover:border-opacity-50`
                    : `bg-white ${colors.borderLight} shadow-sm hover:shadow-md`
                }`}
              >
                <div className={`w-14 h-14 rounded-xl ${colors.bg} flex items-center justify-center shrink-0`}>
                  <platform.icon className={`w-7 h-7 ${isDark ? colors.text : colors.textLight}`} />
                </div>
                <div className="flex-1">
                  <h3 className={`text-xl font-semibold mb-1 transition-colors ${
                    isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
                  }`}>
                    {platform.name}
                  </h3>
                  <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{platform.description}</p>
                  <span className={`text-sm ${isDark ? colors.text : colors.textLight}`}>{platform.members}</span>
                </div>
                <ArrowRight className={`w-5 h-5 shrink-0 transition-colors ${
                  isDark ? 'text-gray-500 group-hover:text-purple-400' : 'text-slate-400 group-hover:text-purple-600'
                }`} />
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* Featured Posts */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Community Highlights</h2>
        <div className="space-y-4">
          {featuredPosts.map((post, index) => (
            <motion.div
              key={post.author}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border ${
                isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-xl shrink-0">
                  {post.avatar}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{post.author}</span>
                    <span className={isDark ? 'text-gray-500' : 'text-slate-400'}>•</span>
                    <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>{post.role}</span>
                  </div>
                  <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{post.content}</p>
                  <div className="flex items-center gap-6 text-sm">
                    <button className={`flex items-center gap-2 transition-colors ${
                      isDark ? 'text-gray-400 hover:text-red-400' : 'text-slate-500 hover:text-red-500'
                    }`}>
                      <Heart className="w-4 h-4" />
                      {post.likes}
                    </button>
                    <button className={`flex items-center gap-2 transition-colors ${
                      isDark ? 'text-gray-400 hover:text-purple-400' : 'text-slate-500 hover:text-purple-600'
                    }`}>
                      <MessageSquare className="w-4 h-4" />
                      {post.replies} replies
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="mb-16">
        <h2 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>Upcoming Events</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {events.map((event, index) => (
            <motion.div
              key={event.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl border transition-all ${
                isDark
                  ? 'bg-dark-800/50 border-white/5 hover:border-purple-500/30'
                  : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'
              }`}
            >
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
              }`}>
                {event.type}
              </span>
              <h3 className={`text-lg font-semibold mt-4 mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{event.title}</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                {event.date} • {event.time}
              </p>
              <button className={`mt-4 text-sm font-medium flex items-center gap-1 ${
                isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
              }`}>
                Register <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contributors */}
      <section className="mb-16">
        <div className={`p-8 rounded-2xl border ${
          isDark
            ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
            : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-purple-100'
        }`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-6 h-6 text-yellow-400" />
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Top Contributors</h2>
              </div>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                Special thanks to our amazing community members who help others every day.
              </p>
            </div>
            <div className="flex -space-x-3">
              {['👨‍💻', '👩‍🎨', '🧑‍💼', '👨‍🔬', '👩‍💼'].map((avatar, i) => (
                <div
                  key={i}
                  className={`w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-xl border-2 ${
                    isDark ? 'border-dark-900' : 'border-white'
                  }`}
                >
                  {avatar}
                </div>
              ))}
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm border-2 ${
                isDark ? 'bg-dark-700 text-white border-dark-900' : 'bg-slate-200 text-slate-700 border-white'
              }`}>
                +42
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark ? 'bg-dark-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'
      }`}>
        <Users className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Ready to Join?</h2>
        <p className={`mb-6 max-w-md mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Join our Discord community to connect with fellow users, get help, and share your wins.
        </p>
        <a
          href="https://discord.gg/flowai"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow"
        >
          Join Discord <ArrowRight className="w-4 h-4" />
        </a>
      </section>
    </StaticPageLayout>
  );
}
